import { useState, useMemo, useEffect, FormEvent, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useAuth } from '../../../stores/authStore';
import { usePermissions, Permissions } from '../hooks/usePermissions';
import { useTenantUsers } from '../hooks/useTenantUsers';
import { UserTable } from '../components/UserTable';
import { RoleSelector } from '../components/RoleSelector';
import { TenantUser } from '../types';
import { customFields as customFieldsApi, admin as adminApi } from '../../../services/api';
import { UDFFieldItem } from './TenantUDFSettingsPage';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { showSuccess, showError } from '../../../utils/alerts';

const DEFAULT_DESIGNATIONS = [
  'Service Advisor',
  'Floor Manager',
  'Technician',
  'Diagnostic Specialist',
  'L&D Coach',
  'Customer Care Lead',
  'Workshop Supervisor',
  'Branch Relationship Officer',
  'Sales Specialist',
];

const DEFAULT_TEAMS = [
  'Body & Paint',
  'Diagnostics',
  'Express Service',
  'General Maintenance',
  'High-Voltage EV Unit',
  'Customer Experience',
  'Branch Banking',
  'Wealth Advisory',
];

const DEFAULT_REGIONS = [
  'West',
  'North',
  'South',
  'East',
  'Central Hub',
  'APAC Overseas',
];

const GRADE_OPTIONS = [
  'Level 1 (Apprentice)',
  'Level 2 (Junior)',
  'Level 3 (Senior)',
  'Level 4 (Master)',
  'Executive / Lead',
];

export function TenantUsersPage() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const canManage = can(Permissions.TENANT_USERS_MANAGE);
  const isSuperAdmin = user?.role === 'PLATFORM_ADMIN' || user?.role === 'SUPER_ADMIN';

  // Tenant list for Super Admin
  const [tenantsList, setTenantsList] = useState<Array<{ id: string; name: string; slug: string }>>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(isSuperAdmin ? 'all' : (user?.tenantId || ''));

  useEffect(() => {
    if (isSuperAdmin) {
      adminApi.tenants.list().then((res: any) => {
        if (res?.success && Array.isArray(res.data?.tenants)) {
          setTenantsList(res.data.tenants);
        }
      }).catch(err => console.warn('Could not load tenants list:', err));
    }
  }, [isSuperAdmin]);

  const activeTenantFilter = selectedTenantId === 'all' ? undefined : selectedTenantId;

  const {
    users,
    isLoading,
    error,
    createUser,
    bulkImportUsers,
    updateUser,
    updateUserRole,
    updateUserStatus,
    resetUserPassword,
  } = useTenantUsers(activeTenantFilter);

  // UDF Taxonomy
  const [activeUdfs, setActiveUdfs] = useState<UDFFieldItem[]>([]);

  useEffect(() => {
    async function loadUdfs() {
      try {
        const res = await customFieldsApi.list(activeTenantFilter);
        if (res.success && res.data?.fields) {
          setActiveUdfs(res.data.fields.filter((f: UDFFieldItem) => f.isActive));
        }
      } catch (err) {
        console.warn('Could not load custom fields:', err);
      }
    }
    loadUdfs();
  }, [activeTenantFilter]);

  // Filters State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDesignation, setSelectedDesignation] = useState('ALL');
  const [selectedTeam, setSelectedTeam] = useState('ALL');
  const [selectedRegion, setSelectedRegion] = useState('ALL');
  const [selectedRole, setSelectedRole] = useState('ALL');
  const [selectedManager, setSelectedManager] = useState('ALL');

  // Add Member Modal State
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalTenantId, setModalTenantId] = useState<string>('');
  const [modalStep, setModalStep] = useState<'SELECT_TENANT' | 'FORM'>('FORM');
  const [modalEligibleManagers, setModalEligibleManagers] = useState<TenantUser[]>([]);
  const [modalUdfs, setModalUdfs] = useState<UDFFieldItem[]>([]);
  const [isLoadingModalContext, setIsLoadingModalContext] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [designation, setDesignation] = useState('Service Advisor');
  const [team, setTeam] = useState('Diagnostics');
  const [region, setRegion] = useState('West');
  const [grade, setGrade] = useState('Level 2 (Junior)');
  const [role, setRole] = useState('LEARNER');
  const [managerId, setManagerId] = useState('');
  const [managerName, setManagerName] = useState('');
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Bulk Import Modal State
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);
  const [importTenantId, setImportTenantId] = useState<string>('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importRawText, setImportRawText] = useState<string>('');
  const [parsedImportRows, setParsedImportRows] = useState<any[]>([]);
  const [importValidationErrors, setImportValidationErrors] = useState<string[]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ total: number; createdCount: number; failedCount: number; created?: any[]; errors?: any[] } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Edit Member Modal State
  const [editingUser, setEditingUser] = useState<TenantUser | null>(null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editEmployeeId, setEditEmployeeId] = useState('');
  const [editDesignation, setEditDesignation] = useState('');
  const [editTeam, setEditTeam] = useState('');
  const [editRegion, setEditRegion] = useState('');
  const [editGrade, setEditGrade] = useState('');
  const [editRole, setEditRole] = useState('LEARNER');
  const [editManagerId, setEditManagerId] = useState('');
  const [editManagerName, setEditManagerName] = useState('');
  const [editCustomFieldValues, setEditCustomFieldValues] = useState<Record<string, any>>({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Reset Password Modal State
  const [resetTargetUser, setResetTargetUser] = useState<TenantUser | null>(null);
  const [resetMode, setResetMode] = useState<'DIRECT' | 'LINK'>('DIRECT');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [generatedResetUrl, setGeneratedResetUrl] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleOpenResetPasswordModal = (u: TenantUser) => {
    setResetTargetUser(u);
    setResetMode('DIRECT');
    setResetNewPassword('');
    setGeneratedResetUrl('');
    setCopySuccess(false);
  };

  const handleExecuteResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!resetTargetUser) return;
    if (resetMode === 'DIRECT' && !resetNewPassword.trim()) {
      showError('Please enter a new password.');
      return;
    }

    setIsResettingPassword(true);
    try {
      const res = await resetUserPassword(
        resetTargetUser.id,
        resetMode === 'DIRECT'
          ? { newPassword: resetNewPassword.trim() }
          : { generateLinkOnly: true },
        activeTenantFilter
      );

      if (res.success) {
        if (resetMode === 'DIRECT') {
          showSuccess(res.message || `Password for ${resetTargetUser.email} has been reset.`);
          setResetTargetUser(null);
        } else {
          const fullUrl = res.data?.resetUrl
            ? `${window.location.origin}${res.data.resetUrl}`
            : (res.data?.token ? `${window.location.origin}/reset-password?token=${res.data.token}` : '');
          setGeneratedResetUrl(fullUrl);
          showSuccess('One-time password reset link generated.');
        }
      } else {
        showError(res.error || 'Failed to reset password.');
      }
    } catch (err: any) {
      showError(err.message || 'Error executing password reset.');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleCopyResetLink = () => {
    if (!generatedResetUrl) return;
    navigator.clipboard.writeText(generatedResetUrl);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 3000);
  };

  // Load tenant context when modal tenant changes
  const loadModalTenantContext = async (targetTenant: string) => {
    setIsLoadingModalContext(true);
    try {
      const [usersRes, udfRes] = await Promise.all([
        adminApi.users.list(targetTenant),
        customFieldsApi.list(targetTenant),
      ]);
      if (usersRes.success && usersRes.data?.users) {
        const mgrs = usersRes.data.users.filter(
          (u: TenantUser) =>
            u.role === 'TENANT_ADMIN' ||
            u.role === 'PLATFORM_ADMIN' ||
            u.role === 'SUPER_ADMIN' ||
            u.role === 'MANAGER' ||
            u.role === 'TRAINER' ||
            (u.designation && u.designation.toLowerCase().includes('manager')) ||
            (u.designation && u.designation.toLowerCase().includes('lead')) ||
            (u.designation && u.designation.toLowerCase().includes('supervisor'))
        );
        setModalEligibleManagers(mgrs);
      }
      if (udfRes.success && udfRes.data?.fields) {
        setModalUdfs(udfRes.data.fields.filter((f: UDFFieldItem) => f.isActive));
      }
    } catch (err) {
      console.warn('Error fetching modal tenant context:', err);
    } finally {
      setIsLoadingModalContext(false);
    }
  };

  // Eligible Managers list from currently loaded users
  const eligibleManagers = useMemo(() => {
    return users.filter(
      (u) =>
        u.role === 'TENANT_ADMIN' ||
        u.role === 'PLATFORM_ADMIN' ||
        u.role === 'SUPER_ADMIN' ||
        u.role === 'MANAGER' ||
        u.role === 'TRAINER' ||
        (u.designation && u.designation.toLowerCase().includes('manager')) ||
        (u.designation && u.designation.toLowerCase().includes('lead')) ||
        (u.designation && u.designation.toLowerCase().includes('supervisor'))
    );
  }, [users]);

  // Unique Managers for Filter
  const availableManagers = useMemo(() => {
    const set = new Set<string>();
    users.forEach((u) => {
      if (u.managerName) set.add(u.managerName);
    });
    return Array.from(set);
  }, [users]);

  // Dynamic Master Taxonomy options derived from tenant users & defaults
  const availableDesignations = useMemo(() => {
    const set = new Set<string>(DEFAULT_DESIGNATIONS);
    users.forEach((u) => {
      if (u.designation) set.add(u.designation);
    });
    return Array.from(set);
  }, [users]);

  const availableTeams = useMemo(() => {
    const set = new Set<string>(DEFAULT_TEAMS);
    users.forEach((u) => {
      if (u.team) set.add(u.team);
    });
    return Array.from(set);
  }, [users]);

  const availableRegions = useMemo(() => {
    const set = new Set<string>(DEFAULT_REGIONS);
    users.forEach((u) => {
      if (u.region) set.add(u.region);
    });
    return Array.from(set);
  }, [users]);

  // Filtered users
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
        const matchesName = fullName.includes(q);
        const matchesEmail = u.email.toLowerCase().includes(q);
        const matchesEmpId = (u.employeeId || '').toLowerCase().includes(q);
        const matchesMgr = (u.managerName || '').toLowerCase().includes(q);
        if (!matchesName && !matchesEmail && !matchesEmpId && !matchesMgr) return false;
      }
      if (selectedDesignation !== 'ALL' && (u.designation || '') !== selectedDesignation) {
        return false;
      }
      if (selectedTeam !== 'ALL' && (u.team || '') !== selectedTeam) {
        return false;
      }
      if (selectedRegion !== 'ALL' && (u.region || '') !== selectedRegion) {
        return false;
      }
      if (selectedRole !== 'ALL' && u.role !== selectedRole) {
        return false;
      }
      if (selectedManager !== 'ALL' && (u.managerName || '') !== selectedManager) {
        return false;
      }
      return true;
    });
  }, [users, searchTerm, selectedDesignation, selectedTeam, selectedRegion, selectedRole, selectedManager]);

  const handleOpenAddModal = () => {
    setEmail('');
    setPassword('');
    setFirstName('');
    setLastName('');
    setEmployeeId(`EMP-${Math.floor(1000 + Math.random() * 9000)}`);
    setDesignation('Service Advisor');
    setTeam('Diagnostics');
    setRegion('West');
    setGrade('Level 2 (Junior)');
    setRole('LEARNER');
    setManagerId('');
    setManagerName('');
    setCustomFieldValues({});
    setFormError(null);

    if (isSuperAdmin && (!selectedTenantId || selectedTenantId === 'all')) {
      setModalTenantId('');
      setModalStep('SELECT_TENANT');
      setModalEligibleManagers([]);
      setModalUdfs([]);
      setShowAddModal(true);
    } else {
      const targetT = selectedTenantId && selectedTenantId !== 'all' ? selectedTenantId : (user?.tenantId || '');
      setModalTenantId(targetT);
      setModalStep('FORM');
      loadModalTenantContext(targetT);
      setShowAddModal(true);
    }
  };

  const handleSelectModalTenant = (tId: string) => {
    setModalTenantId(tId);
    setModalStep('FORM');
    loadModalTenantContext(tId);
  };

  const handleManagerSelect = (selectedId: string) => {
    setManagerId(selectedId);
    if (!selectedId) {
      setManagerName('');
      return;
    }
    const mgrList = modalEligibleManagers.length > 0 ? modalEligibleManagers : eligibleManagers;
    const found = mgrList.find((u) => u.id === selectedId);
    if (found) {
      setManagerName(`${found.firstName} ${found.lastName}`.trim() || found.email);
    }
  };

  const handleEditManagerSelect = (selectedId: string) => {
    setEditManagerId(selectedId);
    if (!selectedId) {
      setEditManagerName('');
      return;
    }
    const found = users.find((u) => u.id === selectedId);
    if (found) {
      setEditManagerName(`${found.firstName} ${found.lastName}`.trim() || found.email);
    }
  };

  const CORE_SYSTEM_FIELD_CODES = new Set([
    'region',
    'designation',
    'managername',
    'managerid',
    'team',
    'grade',
    'role',
    'employeeid',
    'businessunit',
    'branchcode',
    'costcenter',
    'hiredate',
    'email',
    'password',
    'firstname',
    'lastname',
  ]);

  const handleAddUser = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError(null);

    const activeFields = modalUdfs.length > 0 ? modalUdfs : activeUdfs;

    // Validate required custom fields (excluding standard system taxonomy fields)
    for (const udf of activeFields) {
      const codeLower = (udf.code || '').toLowerCase().trim();
      if (udf.required && !udf.isSystem && !CORE_SYSTEM_FIELD_CODES.has(codeLower)) {
        const val = customFieldValues[udf.code];
        if (val === undefined || val === null || val === '') {
          setFormError(`Required field "${udf.name}" cannot be empty.`);
          setIsSubmitting(false);
          return;
        }
      }
    }

    const assignedDesignation = (designation || 'Service Advisor').trim();
    const assignedRole = role || 'LEARNER';
    const assignedTeam = (team || 'Diagnostics').trim();
    const assignedRegion = (region || 'West').trim();
    const assignedGrade = (grade || 'Level 2 (Junior)').trim();

    const completeCustomFields = {
      ...customFieldValues,
      designation: assignedDesignation,
      team: assignedTeam,
      region: assignedRegion,
      grade: assignedGrade,
    };

    const res = await createUser(
      {
        email: email.trim(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        employeeId: employeeId.trim() || `EMP-${Math.floor(1000 + Math.random() * 9000)}`,
        designation: assignedDesignation,
        team: assignedTeam,
        region: assignedRegion,
        grade: assignedGrade,
        role: assignedRole,
        managerId: managerId.trim() || undefined,
        managerName: managerName.trim() || undefined,
        customFields: completeCustomFields,
      },
      modalTenantId || activeTenantFilter
    );
    setIsSubmitting(false);

    if (res.success) {
      setShowAddModal(false);
      showSuccess('Member created successfully');
      setEmail('');
      setPassword('');
      setFirstName('');
      setLastName('');
      setRole('LEARNER');
      setDesignation('Service Advisor');
      setTeam('Diagnostics');
      setRegion('West');
      setGrade('Level 2 (Junior)');
      setManagerId('');
      setManagerName('');
      setCustomFieldValues({});
    } else {
      setFormError(res.error || 'Failed to create user account.');
    }
  };

  const handleOpenEditModal = (u: TenantUser) => {
    setEditingUser(u);
    setEditFirstName(u.firstName || '');
    setEditLastName(u.lastName || '');
    setEditEmployeeId(u.employeeId || '');
    setEditDesignation(u.designation || 'Service Advisor');
    setEditTeam(u.team || 'Diagnostics');
    setEditRegion(u.region || 'West');
    setEditGrade(u.grade || 'Level 2 (Junior)');
    setEditRole(u.role || 'LEARNER');
    setEditManagerId(u.managerId || '');
    setEditManagerName(u.managerName || '');
    setEditCustomFieldValues(u.customFields || {});
    setEditError(null);
  };

  const handleSaveEditUser = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    setIsUpdating(true);
    setEditError(null);

    for (const udf of activeUdfs) {
      const codeLower = (udf.code || '').toLowerCase().trim();
      if (udf.required && !udf.isSystem && !CORE_SYSTEM_FIELD_CODES.has(codeLower)) {
        const val = editCustomFieldValues[udf.code];
        if (val === undefined || val === null || val === '') {
          setEditError(`Required field "${udf.name}" cannot be empty.`);
          setIsUpdating(false);
          return;
        }
      }
    }

    const assignedDesignation = (editDesignation || 'Service Advisor').trim();
    const assignedRole = editRole || 'LEARNER';
    const assignedTeam = (editTeam || 'Diagnostics').trim();
    const assignedRegion = (editRegion || 'West').trim();
    const assignedGrade = (editGrade || 'Level 2 (Junior)').trim();

    const completeCustomFields = {
      ...editCustomFieldValues,
      designation: assignedDesignation,
      team: assignedTeam,
      region: assignedRegion,
      grade: assignedGrade,
    };

    const res = await updateUser(
      editingUser.id,
      {
        firstName: editFirstName.trim(),
        lastName: editLastName.trim(),
        employeeId: editEmployeeId.trim(),
        designation: assignedDesignation,
        team: assignedTeam,
        region: assignedRegion,
        grade: assignedGrade,
        role: assignedRole,
        managerId: editManagerId.trim() || undefined,
        managerName: editManagerName.trim() || undefined,
        customFields: completeCustomFields,
      },
      activeTenantFilter
    );
    setIsUpdating(false);

    if (res.success) {
      setEditingUser(null);
      showSuccess('Member details updated');
    } else {
      setEditError(res.error || 'Failed to update member details.');
    }
  };

  // -------------------------------------------------------------
  // Dynamic CSV Template Download (Standard Fields + Active UDFs)
  // -------------------------------------------------------------
  const handleDownloadTemplate = () => {
    const currentFields = activeUdfs.filter(
      (u) => !u.isSystem && !['region', 'designation', 'managerName'].includes(u.code)
    );

    const standardHeaders = [
      'First Name*',
      'Last Name*',
      'Email Address*',
      'Initial Password*',
      'Employee ID',
      'Role',
      'Designation',
      'Team',
      'Region',
      'Seniority Grade',
      'Reporting Manager Email or Name',
    ];

    const udfHeaders = currentFields.map(
      (f) => `${f.name}${f.required ? '*' : ''} [UDF:${f.code}]`
    );

    const fullHeaders = [...standardHeaders, ...udfHeaders];

    // Sample Row
    const sampleStandard = [
      'John',
      'Doe',
      'john.doe@company.com',
      'Password@2026',
      'EMP-2041',
      'LEARNER',
      'Service Advisor',
      'Diagnostics',
      'West',
      'Level 2 (Junior)',
      'alex.lead@company.com',
    ];

    const sampleUdf = currentFields.map((f) => {
      if (f.type === 'SINGLE_SELECT' || f.type === 'MULTI_SELECT') {
        return f.options[0] || 'Default';
      }
      if (f.type === 'DATE') return '2026-01-15';
      if (f.type === 'NUMBER') return '100';
      if (f.type === 'BOOLEAN') return 'true';
      return `Sample ${f.name}`;
    });

    const sampleRow = [...sampleStandard, ...sampleUdf];

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [
        fullHeaders.map((h) => `"${h.replace(/"/g, '""')}"`).join(','),
        sampleRow.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','),
      ].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    const tenantSlug = activeTenantFilter || user?.tenantSlug || 'organization';
    link.setAttribute('download', `Trainova_Member_Import_Template_${tenantSlug}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSuccess('Import template with custom fields downloaded');
  };

  // -------------------------------------------------------------
  // Dynamic CSV Export (All Members + Active UDFs)
  // -------------------------------------------------------------
  const handleExportUsers = () => {
    if (filteredUsers.length === 0) {
      showError('No members to export.');
      return;
    }

    const currentFields = activeUdfs.filter(
      (u) => !u.isSystem && !['region', 'designation', 'managerName'].includes(u.code)
    );

    const standardHeaders = [
      'User ID',
      'First Name',
      'Last Name',
      'Email Address',
      'Employee ID',
      'Role',
      'Designation',
      'Team',
      'Region',
      'Seniority Grade',
      'Reporting Manager',
      'Active Status',
      'Created At',
    ];

    const udfHeaders = currentFields.map((f) => `${f.name} (${f.code})`);
    const fullHeaders = [...standardHeaders, ...udfHeaders];

    const rows = filteredUsers.map((u) => {
      const stdValues = [
        u.id,
        u.firstName || '',
        u.lastName || '',
        u.email,
        u.employeeId || '',
        u.role,
        u.designation || '',
        u.team || '',
        u.region || '',
        u.grade || '',
        u.managerName || '',
        u.isActive ? 'Active' : 'Inactive',
        u.createdAt || '',
      ];

      const udfValues = currentFields.map((f) => {
        const val = u.customFields?.[f.code];
        if (val === undefined || val === null) return '';
        return String(val);
      });

      return [...stdValues, ...udfValues];
    });

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [
        fullHeaders.map((h) => `"${h.replace(/"/g, '""')}"`).join(','),
        ...rows.map((row) => row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')),
      ].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    const tenantSlug = activeTenantFilter || user?.tenantSlug || 'organization';
    link.setAttribute('download', `Trainova_Members_Export_${tenantSlug}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSuccess(`Exported ${filteredUsers.length} members successfully.`);
  };

  // -------------------------------------------------------------
  // Bulk Import Parsing & Handler (Supports Excel .xlsx, .xls, CSV, TSV & Text)
  // -------------------------------------------------------------
  const parseRowsFromMatrix = (matrix: any[][]) => {
    if (!matrix || matrix.length < 2) {
      setImportValidationErrors(['The file must contain a header row and at least one data row.']);
      setParsedImportRows([]);
      return;
    }

    // Clean headers and strip UTF-8 BOM
    const rawHeaders = matrix[0].map((h: any) => String(h || '').replace(/^\uFEFF/, '').trim());
    const errors: string[] = [];
    const parsed: any[] = [];

    // Map headers to fields
    const headerMap: Record<number, { field: string; isUdf: boolean; code?: string }> = {};

    rawHeaders.forEach((h: string, idx: number) => {
      const lower = h.toLowerCase().trim();
      if (!lower) return;

      if (lower.includes('[udf:')) {
        const match = h.match(/\[udf:([^\]]+)\]/i);
        if (match) {
          headerMap[idx] = { field: 'customFields', isUdf: true, code: match[1].trim() };
          return;
        }
      }

      if (
        lower.includes('email') ||
        lower.includes('e-mail') ||
        lower.includes('mail id') ||
        lower === 'mail' ||
        lower.includes('official email') ||
        lower.includes('work email') ||
        lower.includes('user email') ||
        lower.includes('learner email') ||
        lower === 'email address' ||
        lower === 'username' ||
        lower === 'login'
      ) {
        headerMap[idx] = { field: 'email', isUdf: false };
      } else if (
        lower.includes('first name') ||
        lower === 'firstname' ||
        lower === 'first_name' ||
        lower === 'fname' ||
        lower === 'given name'
      ) {
        headerMap[idx] = { field: 'firstName', isUdf: false };
      } else if (
        lower.includes('last name') ||
        lower === 'lastname' ||
        lower === 'last_name' ||
        lower === 'lname' ||
        lower === 'surname' ||
        lower === 'family name'
      ) {
        headerMap[idx] = { field: 'lastName', isUdf: false };
      } else if (
        lower === 'name' ||
        lower === 'full name' ||
        lower === 'fullname' ||
        lower === 'learner name' ||
        lower === 'employee name' ||
        lower === 'member name'
      ) {
        headerMap[idx] = { field: 'fullName', isUdf: false };
      } else if (
        lower.includes('password') ||
        lower === 'initial password' ||
        lower === 'pwd' ||
        lower === 'pass'
      ) {
        headerMap[idx] = { field: 'password', isUdf: false };
      } else if (
        lower.includes('employee id') ||
        lower.includes('empid') ||
        lower.includes('emp id') ||
        lower.includes('badge') ||
        lower === 'staff id' ||
        lower === 'emp code' ||
        lower === 'employee code'
      ) {
        headerMap[idx] = { field: 'employeeId', isUdf: false };
      } else if (
        lower === 'role' ||
        lower.includes('assigned role') ||
        lower.includes('user role') ||
        lower.includes('access level')
      ) {
        headerMap[idx] = { field: 'role', isUdf: false };
      } else if (
        lower.includes('designation') ||
        lower.includes('job title') ||
        lower.includes('role title') ||
        lower.includes('position') ||
        lower === 'title' ||
        lower === 'job'
      ) {
        headerMap[idx] = { field: 'designation', isUdf: false };
      } else if (
        lower.includes('team') ||
        lower.includes('department') ||
        lower.includes('dept') ||
        lower.includes('unit') ||
        lower.includes('division')
      ) {
        headerMap[idx] = { field: 'team', isUdf: false };
      } else if (
        lower.includes('region') ||
        lower.includes('hub') ||
        lower.includes('territory') ||
        lower.includes('zone') ||
        lower.includes('location') ||
        lower.includes('branch')
      ) {
        headerMap[idx] = { field: 'region', isUdf: false };
      } else if (
        lower.includes('grade') ||
        lower.includes('seniority') ||
        lower.includes('level') ||
        lower.includes('band')
      ) {
        headerMap[idx] = { field: 'grade', isUdf: false };
      } else if (
        lower.includes('manager') ||
        lower.includes('supervisor') ||
        lower.includes('reporting to') ||
        lower.includes('lead')
      ) {
        headerMap[idx] = { field: 'managerName', isUdf: false };
      } else {
        // Try matching against active UDFs by code or name
        const foundUdf = activeUdfs.find(
          (u) =>
            u.code.toLowerCase() === lower ||
            u.name.toLowerCase() === lower ||
            lower.includes(u.code.toLowerCase()) ||
            lower.includes(u.name.toLowerCase())
        );
        if (foundUdf) {
          headerMap[idx] = { field: 'customFields', isUdf: true, code: foundUdf.code };
        }
      }
    });

    for (let r = 1; r < matrix.length; r++) {
      const rowValues = matrix[r];
      if (!rowValues || (Array.isArray(rowValues) && rowValues.every((v) => v === undefined || v === null || String(v).trim() === ''))) {
        continue;
      }

      const userObj: any = { customFields: {} };

      rowValues.forEach((val: any, idx: number) => {
        const mapping = headerMap[idx];
        if (!mapping) return;
        const strVal = val !== undefined && val !== null ? String(val).trim() : '';
        if (mapping.isUdf && mapping.code) {
          userObj.customFields[mapping.code] = strVal;
        } else {
          userObj[mapping.field] = strVal;
        }
      });

      // Split full name if firstName/lastName were not individually provided
      if (userObj.fullName && (!userObj.firstName || !userObj.lastName)) {
        const parts = userObj.fullName.split(/\s+/);
        if (!userObj.firstName) userObj.firstName = parts[0] || '';
        if (!userObj.lastName) userObj.lastName = parts.slice(1).join(' ') || '';
      }

      // If email wasn't matched via header, search row values for an email address
      if (!userObj.email) {
        const foundEmail = rowValues.find((v: any) => typeof v === 'string' && v.includes('@') && v.includes('.'));
        if (foundEmail) {
          userObj.email = String(foundEmail).trim();
        }
      }

      // Default values
      if (!userObj.password) userObj.password = 'Trainova@2026!';
      if (!userObj.role) userObj.role = 'LEARNER';
      if (!userObj.designation) userObj.designation = 'Service Advisor';
      if (!userObj.team) userObj.team = 'Diagnostics';
      if (!userObj.region) userObj.region = 'West';
      if (!userObj.grade) userObj.grade = 'Level 2 (Junior)';
      if (!userObj.employeeId) userObj.employeeId = `EMP-${Math.floor(1000 + Math.random() * 9000)}`;

      // Validation
      const isEmailValid = userObj.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userObj.email);
      const isNamePresent = Boolean(userObj.firstName);

      if (!userObj.email) {
        errors.push(`Row ${r}: Missing email address.`);
      } else if (!isEmailValid) {
        errors.push(`Row ${r}: Invalid email format ("${userObj.email}").`);
      }

      if (!isNamePresent) {
        errors.push(`Row ${r}: Missing first name.`);
      }

      userObj._isValid = Boolean(userObj.email && isEmailValid && isNamePresent);
      parsed.push(userObj);
    }

    setParsedImportRows(parsed);
    setImportValidationErrors(errors);
  };

  const parseCSVText = (text: string) => {
    if (!text || !text.trim()) {
      setParsedImportRows([]);
      setImportValidationErrors([]);
      return;
    }
    try {
      const workbook = XLSX.read(text, { type: 'string', raw: true });
      const firstSheet = workbook.SheetNames[0];
      if (firstSheet) {
        const matrix = XLSX.utils.sheet_to_json<any[]>(workbook.Sheets[firstSheet], { header: 1 });
        parseRowsFromMatrix(matrix);
        return;
      }
    } catch {
      // Fallback manual line splitter
      const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
      const delimiter = text.includes('\t') ? '\t' : (text.includes(';') ? ';' : ',');
      const matrix = lines.map((l) => l.split(delimiter).map((c) => c.replace(/^["']|["']$/g, '').trim()));
      parseRowsFromMatrix(matrix);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const buffer = evt.target?.result as ArrayBuffer;
        const workbook = XLSX.read(buffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        if (firstSheetName) {
          const sheet = workbook.Sheets[firstSheetName];
          const matrix = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1 });
          parseRowsFromMatrix(matrix);
          // Also set CSV representation in raw text box for visibility
          const csvText = XLSX.utils.sheet_to_csv(sheet);
          setImportRawText(csvText);
        } else {
          setImportValidationErrors(['No readable sheet found in uploaded file.']);
          setParsedImportRows([]);
        }
      } catch (err: any) {
        console.warn('Error reading Excel/CSV file:', err);
        setImportValidationErrors([`Failed to read file: ${err.message || 'Invalid format'}`]);
        setParsedImportRows([]);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleOpenBulkImportModal = () => {
    const targetT = selectedTenantId && selectedTenantId !== 'all' ? selectedTenantId : (user?.tenantId || '');
    setImportTenantId(targetT);
    setImportFile(null);
    setImportRawText('');
    setParsedImportRows([]);
    setImportValidationErrors([]);
    setImportResult(null);
    setShowBulkImportModal(true);
  };

  const handleExecuteBulkImport = async () => {
    if (parsedImportRows.length === 0) {
      showError('No valid rows found to import.');
      return;
    }
    const targetT = importTenantId || activeTenantFilter || user?.tenantId;
    if (!targetT) {
      showError('Please select a target organization/tenant for the import.');
      return;
    }

    setIsImporting(true);
    const res = await bulkImportUsers(parsedImportRows, targetT);
    setIsImporting(false);

    if (res.success && res.data) {
      setImportResult(res.data);
      showSuccess(`Imported ${res.data.createdCount} organization members.`);
    } else {
      showError(res.error || 'Failed to complete member import.');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedDesignation('ALL');
    setSelectedTeam('ALL');
    setSelectedRegion('ALL');
    setSelectedRole('ALL');
    setSelectedManager('ALL');
  };

  const hasActiveFilters =
    searchTerm !== '' ||
    selectedDesignation !== 'ALL' ||
    selectedTeam !== 'ALL' ||
    selectedRegion !== 'ALL' ||
    selectedRole !== 'ALL' ||
    selectedManager !== 'ALL';

  return (
    <div className="admin-page">
      <AdminPageHeader
        breadcrumbs={[
          { label: 'Admin', path: '/admin' },
          { label: 'User Directory' },
        ]}
        icon="👥"
        title="Organization Members & User Directory"
        badge="Access & Governance"
        description={`Manage learner access, team assignments, designations, manager hierarchies, and enterprise roles for ${user?.tenantSlug ?? user?.tenantId ?? 'Current'}.`}
        actions={
          <div className="tenant-users-header-actions">
            <button
              onClick={handleDownloadTemplate}
              className="admin-btn admin-btn--secondary"
              title="Download dynamic CSV template with active custom fields"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                fontWeight: 600,
                padding: '9px 14px',
              }}
            >
              📥 Download Template
            </button>

            {canManage && (
              <button
                onClick={handleOpenBulkImportModal}
                className="admin-btn admin-btn--secondary"
                title="Bulk import members from CSV or Excel"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  padding: '9px 14px',
                }}
              >
                📂 Import Members
              </button>
            )}

            <button
              onClick={handleExportUsers}
              className="admin-btn admin-btn--secondary"
              title="Export filtered member directory with custom fields"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px',
                fontWeight: 600,
                padding: '9px 14px',
              }}
            >
              📤 Export CSV
            </button>

            {canManage && (
              <button
                onClick={handleOpenAddModal}
                className="admin-btn admin-btn--primary tenant-users-header-actions__primary"
                style={{
                  background: '#2563eb',
                  color: '#ffffff',
                  border: 'none',
                  padding: '9px 16px',
                  borderRadius: '8px',
                  fontWeight: 700,
                  fontSize: '13.5px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(37, 99, 235, 0.4)',
                }}
              >
                <span style={{ fontSize: '16px', lineHeight: 1 }}>+</span> Add Member
              </button>
            )}
          </div>
        }
      />

      {error && <div className="admin-alert admin-alert--error">{error}</div>}

      {/* Super Admin Tenant Switcher */}
      {isSuperAdmin && (
        <div
          className="tenant-users-scope-banner"
          style={{
            background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, rgba(30, 58, 138, 0.08) 100%)',
            border: '1px solid rgba(37, 99, 235, 0.2)',
            borderRadius: '12px',
            padding: '14px 20px',
            marginBottom: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>🏢</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: '13.5px', color: '#1e3a8a' }}>
                Super Admin Scope: Active Organization Filter
              </div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
                Viewing directory and taxonomy specific to the selected enterprise tenant.
              </div>
            </div>
          </div>
          <div className="tenant-users-scope-controls" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ fontSize: '13px', fontWeight: 600, color: '#1e3a8a' }}>Organization:</label>
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              className="admin-form__select tenant-users-scope-select"
              style={{ padding: '8px 12px', fontSize: '13px', borderColor: '#93c5fd', background: '#fff' }}
            >
              <option value="all">🌐 All Organizations (Cross-Tenant)</option>
              {tenantsList.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.slug || t.id})
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Dynamic Data-Driven Filter Bar */}
      <div
        className="tenant-users-filter-bar"
        style={{
          background: 'var(--white)',
          border: '1px solid var(--line)',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '20px',
          boxShadow: '0 1px 3px rgba(7, 29, 69, 0.04)',
        }}
      >
        <div
          className="tenant-users-filter-grid"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: '14px', alignItems: 'flex-end' }}
        >
          {/* Search Input */}
          <div className="admin-form__group" style={{ margin: 0 }}>
            <label className="admin-form__label" style={{ fontSize: '12px' }}>Search Member</label>
            <input
              type="text"
              placeholder="Name, email, or emp ID…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="admin-form__input"
              style={{ padding: '7px 12px', fontSize: '13px' }}
            />
          </div>

          {/* Designation Filter */}
          <div className="admin-form__group" style={{ margin: 0 }}>
            <label className="admin-form__label" style={{ fontSize: '12px' }}>Designation</label>
            <select
              value={selectedDesignation}
              onChange={(e) => setSelectedDesignation(e.target.value)}
              className="admin-form__select"
              style={{ padding: '7px 12px', fontSize: '13px' }}
            >
              <option value="ALL">All Designations</option>
              {availableDesignations.map((des) => (
                <option key={des} value={des}>
                  {des}
                </option>
              ))}
            </select>
          </div>

          {/* Team Filter */}
          <div className="admin-form__group" style={{ margin: 0 }}>
            <label className="admin-form__label" style={{ fontSize: '12px' }}>Team</label>
            <select
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.target.value)}
              className="admin-form__select"
              style={{ padding: '7px 12px', fontSize: '13px' }}
            >
              <option value="ALL">All Teams</option>
              {availableTeams.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          {/* Region Filter */}
          <div className="admin-form__group" style={{ margin: 0 }}>
            <label className="admin-form__label" style={{ fontSize: '12px' }}>Region</label>
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="admin-form__select"
              style={{ padding: '7px 12px', fontSize: '13px' }}
            >
              <option value="ALL">All Regions</option>
              {availableRegions.map((reg) => (
                <option key={reg} value={reg}>
                  {reg}
                </option>
              ))}
            </select>
          </div>

          {/* Manager Filter */}
          <div className="admin-form__group" style={{ margin: 0 }}>
            <label className="admin-form__label" style={{ fontSize: '12px' }}>Reporting Manager</label>
            <select
              value={selectedManager}
              onChange={(e) => setSelectedManager(e.target.value)}
              className="admin-form__select"
              style={{ padding: '7px 12px', fontSize: '13px' }}
            >
              <option value="ALL">All Managers</option>
              {availableManagers.map((mgr) => (
                <option key={mgr} value={mgr}>
                  {mgr}
                </option>
              ))}
            </select>
          </div>

          {/* Role Filter */}
          <div className="admin-form__group" style={{ margin: 0 }}>
            <label className="admin-form__label" style={{ fontSize: '12px' }}>Role</label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="admin-form__select"
              style={{ padding: '7px 12px', fontSize: '13px' }}
            >
              <option value="ALL">All Roles</option>
              <option value="LEARNER">Learner</option>
              <option value="TRAINER">Instructor</option>
              <option value="MANAGER">Manager</option>
              <option value="TENANT_ADMIN">Tenant Admin</option>
              <option value="PLATFORM_ADMIN">Super Admin</option>
            </select>
          </div>

          {/* Clear Filters Button */}
          {hasActiveFilters && (
            <div>
              <button
                type="button"
                onClick={clearFilters}
                className="admin-btn admin-btn--secondary"
                style={{ padding: '7px 14px', fontSize: '13px', whiteSpace: 'nowrap' }}
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Filter Summary */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
          <div style={{ fontSize: '12px', color: 'var(--muted)' }}>
            Filter: matching <strong>{filteredUsers.length}</strong> of <strong>{users.length}</strong> total registered members
          </div>
        </div>
      </div>

      {isLoading ? (
        <div
          style={{
            background: 'var(--white)',
            border: '1px solid var(--line)',
            borderRadius: '16px',
            padding: '60px 24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '16px',
            minHeight: '380px',
            boxShadow: '0 1px 3px rgba(7, 29, 69, 0.04)',
          }}
        >
          <div
            className="dashboard-spinner"
            style={{ width: '40px', height: '40px', borderWidth: '3px', borderColor: '#2563eb', borderTopColor: 'transparent' }}
          />
          <div style={{ textAlign: 'center' }}>
            <h4 style={{ margin: '0 0 6px', fontSize: '16px', fontWeight: 700, color: 'var(--ink)' }}>
              Loading Organization Members...
            </h4>
            <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--muted)' }}>
              Retrieving member directories, roles, department hierarchies, and custom attributes.
            </p>
          </div>
        </div>
      ) : (
        <UserTable
          users={filteredUsers}
          onRoleChange={async (u, r) => {
            await updateUserRole(u.id, r, activeTenantFilter);
          }}
          onToggleStatus={async (u) => {
            await updateUserStatus(u.id, !u.isActive, activeTenantFilter);
          }}
          onEditUser={(u) => handleOpenEditModal(u)}
          onResetPassword={(u) => handleOpenResetPasswordModal(u)}
          canManageUsers={canManage}
        />
      )}

      {/* Add New Member Modal */}
      {showAddModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal" style={{ maxWidth: '700px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
            {/* Step 1 for Super Admin: Select Tenant if not preselected */}
            {modalStep === 'SELECT_TENANT' ? (
              <div className="admin-form">
                <h3 className="admin-form__title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>🏢</span> Step 1: Select Target Organization
                </h3>
                <p style={{ fontSize: '13.5px', color: 'var(--muted)', marginTop: '-8px', marginBottom: '16px' }}>
                  As a Super Admin, please choose which organization/tenant this new member belongs to.
                  The reporting managers, custom fields (UDFs), and department taxonomies will load specifically for that organization.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                  {tenantsList.map((t) => (
                    <div
                      key={t.id}
                      onClick={() => handleSelectModalTenant(t.id)}
                      style={{
                        padding: '14px 18px',
                        border: '1px solid var(--line)',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.15s ease',
                        background: '#ffffff',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#2563eb';
                        e.currentTarget.style.background = '#f8faff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--line)';
                        e.currentTarget.style.background = '#ffffff';
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--ink)' }}>{t.name}</div>
                        <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Tenant ID: {t.id} · Slug: {t.slug || 'default'}</div>
                      </div>
                      <span style={{ fontSize: '14px', color: '#2563eb', fontWeight: 700 }}>Select &rarr;</span>
                    </div>
                  ))}
                </div>

                <div className="admin-form__actions">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="admin-btn admin-btn--secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleAddUser} className="admin-form">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <h3 className="admin-form__title" style={{ margin: 0 }}>Add New Organization Member</h3>
                  {isSuperAdmin && (
                    <span style={{ fontSize: '12px', background: '#eff6ff', color: '#1d4ed8', padding: '4px 10px', borderRadius: '6px', fontWeight: 600 }}>
                      Org: {tenantsList.find(t => t.id === modalTenantId)?.name || modalTenantId || 'Default'}
                    </span>
                  )}
                </div>

                {isLoadingModalContext && (
                  <div style={{ padding: '12px', background: '#f8faff', borderRadius: '8px', fontSize: '12px', color: '#2563eb', marginBottom: '12px' }}>
                    Loading organization hierarchy &amp; custom attributes…
                  </div>
                )}

                {formError && <div className="admin-alert admin-alert--error">{formError}</div>}

                {/* 1. Identity & Credentials */}
                <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--navy)', borderBottom: '1px solid var(--line)', paddingBottom: '6px', marginBottom: '12px' }}>
                  1. Identity &amp; Credentials
                </div>

                <div className="admin-form__row">
                  <div className="admin-form__group">
                    <label htmlFor="user-fname" className="admin-form__label">
                      First Name <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <input
                      id="user-fname"
                      type="text"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="Alex"
                      className="admin-form__input"
                    />
                  </div>

                  <div className="admin-form__group">
                    <label htmlFor="user-lname" className="admin-form__label">
                      Last Name <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <input
                      id="user-lname"
                      type="text"
                      required
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Learner"
                      className="admin-form__input"
                    />
                  </div>
                </div>

                <div className="admin-form__row">
                  <div className="admin-form__group">
                    <label htmlFor="user-email" className="admin-form__label">
                      Email Address <span style={{ color: '#EF4444' }}>*</span>
                    </label>
                    <input
                      id="user-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="alex@company.com"
                      className="admin-form__input"
                    />
                  </div>

                  <div className="admin-form__group">
                    <label htmlFor="user-empid" className="admin-form__label">
                      Employee ID
                    </label>
                    <input
                      id="user-empid"
                      type="text"
                      value={employeeId}
                      onChange={(e) => setEmployeeId(e.target.value)}
                      placeholder="EMP-1048"
                      className="admin-form__input"
                    />
                  </div>
                </div>

                <div className="admin-form__group">
                  <label htmlFor="user-password" className="admin-form__label">
                    Initial Password <span style={{ color: '#EF4444' }}>*</span>
                  </label>
                  <input
                    id="user-password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min 12 characters"
                    className="admin-form__input"
                  />
                  <small className="admin-form__hint">
                    Must meet organizational governance rules (uppercase, numbers, symbols).
                  </small>
                </div>

                {/* 2. Organization & Hierarchy */}
                <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--navy)', borderBottom: '1px solid var(--line)', paddingBottom: '6px', margin: '18px 0 12px' }}>
                  2. Organization &amp; Hierarchy (UDF)
                </div>

                {/* Reporting Manager Selection */}
                <div className="admin-form__group">
                  <label className="admin-form__label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    👔 Reporting Manager
                    <span style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 400 }}>(Supervisor for evaluations &amp; escalations)</span>
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <select
                      value={managerId}
                      onChange={(e) => handleManagerSelect(e.target.value)}
                      className="admin-form__select"
                    >
                      <option value="">— Select from Tenant Managers —</option>
                      {(modalEligibleManagers.length > 0 ? modalEligibleManagers : eligibleManagers).map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.firstName} {m.lastName} ({m.role} • {m.email})
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      placeholder="Or enter Manager Name..."
                      value={managerName}
                      onChange={(e) => {
                        setManagerName(e.target.value);
                        if (managerId) setManagerId('');
                      }}
                      className="admin-form__input"
                    />
                  </div>
                </div>

                <div className="admin-form__row">
                  <div className="admin-form__group">
                    <label className="admin-form__label">Designation</label>
                    <select
                      value={designation}
                      onChange={(e) => setDesignation(e.target.value)}
                      className="admin-form__select"
                    >
                      {availableDesignations.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="admin-form__group">
                    <label className="admin-form__label">Team / Unit</label>
                    <select
                      value={team}
                      onChange={(e) => setTeam(e.target.value)}
                      className="admin-form__select"
                    >
                      {availableTeams.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="admin-form__row">
                  <div className="admin-form__group">
                    <label className="admin-form__label">Region / Hub</label>
                    <select
                      value={region}
                      onChange={(e) => setRegion(e.target.value)}
                      className="admin-form__select"
                    >
                      {availableRegions.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="admin-form__group">
                    <label className="admin-form__label">Seniority Grade</label>
                    <select
                      value={grade}
                      onChange={(e) => setGrade(e.target.value)}
                      className="admin-form__select"
                    >
                      {GRADE_OPTIONS.map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* 3. Dynamic Custom UDF Fields */}
                {(modalUdfs.length > 0 ? modalUdfs : activeUdfs).filter(
                  (u) => !u.isSystem && !['region', 'designation', 'managerName'].includes(u.code)
                ).length > 0 && (
                  <>
                    <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--navy)', borderBottom: '1px solid var(--line)', paddingBottom: '6px', margin: '18px 0 12px' }}>
                      3. Custom Metadata Attributes (UDF)
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                      {(modalUdfs.length > 0 ? modalUdfs : activeUdfs)
                        .filter((u) => !u.isSystem && !['region', 'designation', 'managerName'].includes(u.code))
                        .map((udf) => (
                          <div key={udf.id} className="admin-form__group" style={{ margin: 0 }}>
                            <label className="admin-form__label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {udf.name}
                              {udf.required && <span style={{ color: '#EF4444', fontWeight: 800 }}>*</span>}
                              <span style={{ fontSize: '10.5px', color: 'var(--muted)', background: 'var(--surface)', padding: '1px 5px', borderRadius: '4px' }}>
                                {udf.category}
                              </span>
                            </label>

                            {udf.type === 'SINGLE_SELECT' || udf.type === 'MULTI_SELECT' ? (
                              <select
                                value={customFieldValues[udf.code] || ''}
                                onChange={(e) => setCustomFieldValues({ ...customFieldValues, [udf.code]: e.target.value })}
                                className="admin-form__select"
                              >
                                <option value="">Select {udf.name}...</option>
                                {udf.options.map((opt, i) => (
                                  <option key={i} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            ) : udf.type === 'DATE' ? (
                              <input
                                type="date"
                                value={customFieldValues[udf.code] || ''}
                                onChange={(e) => setCustomFieldValues({ ...customFieldValues, [udf.code]: e.target.value })}
                                className="admin-form__input"
                              />
                            ) : udf.type === 'NUMBER' ? (
                              <input
                                type="number"
                                placeholder={`Enter ${udf.name}...`}
                                value={customFieldValues[udf.code] || ''}
                                onChange={(e) => setCustomFieldValues({ ...customFieldValues, [udf.code]: e.target.value })}
                                className="admin-form__input"
                              />
                            ) : udf.type === 'BOOLEAN' ? (
                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', paddingTop: '6px' }}>
                                <input
                                  type="checkbox"
                                  checked={Boolean(customFieldValues[udf.code])}
                                  onChange={(e) => setCustomFieldValues({ ...customFieldValues, [udf.code]: e.target.checked })}
                                />
                                Enable {udf.name}
                              </label>
                            ) : (
                              <input
                                type="text"
                                placeholder={`Enter ${udf.name}...`}
                                value={customFieldValues[udf.code] || ''}
                                onChange={(e) => setCustomFieldValues({ ...customFieldValues, [udf.code]: e.target.value })}
                                className="admin-form__input"
                              />
                            )}
                          </div>
                        ))}
                    </div>
                  </>
                )}

                {/* 4. Role Assignment */}
                <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--navy)', borderBottom: '1px solid var(--line)', paddingBottom: '6px', margin: '18px 0 12px' }}>
                  4. Governance &amp; Role
                </div>
                <div className="admin-form__group">
                  <label className="admin-form__label">Assigned Role</label>
                  <RoleSelector currentRole={role} onChange={setRole} />
                </div>

                <div className="admin-form__actions">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    disabled={isSubmitting}
                    className="admin-btn admin-btn--secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !email || !password || !firstName}
                    className="admin-btn admin-btn--primary"
                  >
                    {isSubmitting ? 'Creating…' : 'Create Member'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkImportModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal" style={{ maxWidth: '800px', width: '92%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="admin-form">
              <h3 className="admin-form__title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>📂</span> Bulk Import Organization Members
              </h3>
              <p style={{ fontSize: '13.5px', color: 'var(--muted)', marginTop: '-8px', marginBottom: '16px' }}>
                Upload a CSV file or paste formatted CSV data. Dynamic custom fields (UDFs) and taxonomy attributes will be automatically mapped and validated.
              </p>

              {/* Tenant selector if super admin */}
              {isSuperAdmin && (
                <div className="admin-form__group" style={{ marginBottom: '16px' }}>
                  <label className="admin-form__label">Target Organization for Import <span style={{ color: '#EF4444' }}>*</span></label>
                  <select
                    value={importTenantId}
                    onChange={(e) => {
                      setImportTenantId(e.target.value);
                      loadModalTenantContext(e.target.value);
                    }}
                    className="admin-form__select"
                  >
                    <option value="">— Select Target Organization —</option>
                    {tenantsList.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} ({t.slug || t.id})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Upload Dropzone */}
              <div
                style={{
                  border: '2px dashed var(--line)',
                  borderRadius: '12px',
                  padding: '24px',
                  textAlign: 'center',
                  background: 'var(--surface)',
                  marginBottom: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv,.tsv,.txt"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
                <div style={{ fontSize: '32px', marginBottom: '6px' }}>📊</div>
                <div style={{ fontWeight: 700, fontSize: '14.5px', color: 'var(--ink)' }}>
                  {importFile ? `Uploaded: ${importFile.name}` : 'Click or Drag to Upload Excel (.xlsx, .xls) or CSV'}
                </div>
                <div style={{ fontSize: '12.5px', color: 'var(--muted)', marginTop: '4px' }}>
                  Supports Microsoft Excel (.xlsx, .xls), CSV, TSV with dynamic header detection &amp; [UDF:code] columns
                </div>
              </div>

              {/* Direct Paste Fallback */}
              <div className="admin-form__group">
                <label className="admin-form__label">Or Paste Excel / CSV Tabular Data Directly</label>
                <textarea
                  rows={4}
                  value={importRawText}
                  placeholder={`First Name*,Last Name*,Email Address*,Initial Password*,Employee ID,Role,Designation,Team,Region,Seniority Grade,Reporting Manager Email or Name\nJane,Smith,jane.smith@example.com,Welcome@2026,EMP-3091,LEARNER,Floor Manager,Diagnostics,West,Level 2 (Junior),lead@example.com`}
                  onChange={(e) => {
                    setImportRawText(e.target.value);
                    parseCSVText(e.target.value);
                  }}
                  className="admin-form__input"
                  style={{ fontFamily: 'monospace', fontSize: '12px', whiteSpace: 'pre' }}
                />
              </div>

              {/* Validation Feedback */}
              {importValidationErrors.length > 0 && (
                <div className="admin-alert admin-alert--error" style={{ fontSize: '12.5px', marginBottom: '14px' }}>
                  <strong>Validation Notices ({importValidationErrors.length}):</strong>
                  <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                    {importValidationErrors.slice(0, 5).map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                    {importValidationErrors.length > 5 && <li>...and {importValidationErrors.length - 5} more issues</li>}
                  </ul>
                </div>
              )}

              {/* Preview Table */}
              {parsedImportRows.length > 0 && (
                <div style={{ marginTop: '14px', marginBottom: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontWeight: 700, fontSize: '13.5px', color: 'var(--ink)' }}>
                      Parsed Preview: <strong>{parsedImportRows.length}</strong> member(s) detected
                    </span>
                    <span
                      style={{
                        fontSize: '11.5px',
                        color: parsedImportRows.every((r) => r._isValid !== false) ? '#16a34a' : '#ea580c',
                        fontWeight: 700,
                        background: parsedImportRows.every((r) => r._isValid !== false) ? '#dcfce7' : '#ffedd5',
                        padding: '2px 8px',
                        borderRadius: '6px',
                      }}
                    >
                      {parsedImportRows.every((r) => r._isValid !== false)
                        ? '✓ All rows valid & ready'
                        : `⚠ ${parsedImportRows.filter((r) => r._isValid === false).length} row(s) need attention`}
                    </span>
                  </div>

                  <div style={{ maxHeight: '220px', overflowY: 'auto', overflowX: 'auto', WebkitOverflowScrolling: 'touch', border: '1px solid var(--line)', borderRadius: '8px', background: '#fff' }}>
                    <table style={{ width: '100%', minWidth: '640px', borderCollapse: 'collapse', fontSize: '12px' }}>
                      <thead>
                        <tr style={{ background: '#f8fafd', borderBottom: '1px solid var(--line)', position: 'sticky', top: 0, zIndex: 1 }}>
                          <th style={{ padding: '8px 10px', textAlign: 'left', width: '36px' }}>#</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left' }}>Member Name</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left' }}>Email Address</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left' }}>Role</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left' }}>Designation</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left' }}>Team · Region</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left' }}>Manager</th>
                          <th style={{ padding: '8px 10px', textAlign: 'center' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedImportRows.map((row, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: row._isValid === false ? '#fef2f2' : 'transparent' }}>
                            <td style={{ padding: '8px 10px', color: 'var(--muted)' }}>{idx + 1}</td>
                            <td style={{ padding: '8px 10px', fontWeight: 600, color: '#1e293b' }}>
                              {row.firstName ? `${row.firstName} ${row.lastName || ''}`.trim() : (
                                <span style={{ color: '#ef4444', fontStyle: 'italic' }}>Missing name</span>
                              )}
                            </td>
                            <td style={{ padding: '8px 10px' }}>
                              {row.email ? (
                                <span style={{ fontWeight: 600, color: '#0369a1', background: '#f0f9ff', padding: '2px 6px', borderRadius: '4px' }}>
                                  {row.email}
                                </span>
                              ) : (
                                <span style={{ color: '#ef4444', fontWeight: 700, background: '#fee2e2', padding: '2px 6px', borderRadius: '4px' }}>
                                  Missing email
                                </span>
                              )}
                            </td>
                            <td style={{ padding: '8px 10px' }}>
                              <span style={{ fontSize: '11px', fontWeight: 600, color: '#475569', background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>
                                {row.role || 'LEARNER'}
                              </span>
                            </td>
                            <td style={{ padding: '8px 10px', color: '#334155' }}>{row.designation || 'Service Advisor'}</td>
                            <td style={{ padding: '8px 10px', color: '#64748b' }}>
                              {(row.team || 'Diagnostics')} {row.region ? `· ${row.region}` : ''}
                            </td>
                            <td style={{ padding: '8px 10px', color: '#64748b' }}>{row.managerName || '—'}</td>
                            <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                              {row._isValid !== false ? (
                                <span style={{ color: '#16a34a', fontWeight: 700, fontSize: '11px' }}>✓ Ready</span>
                              ) : (
                                <span style={{ color: '#dc2626', fontWeight: 700, fontSize: '11px' }}>⚠ Check</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Import Result Summary */}
              {importResult && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 16px', marginBottom: '14px' }}>
                  <div style={{ fontWeight: 700, color: '#15803d', fontSize: '13px' }}>
                    🎉 Import Process Completed
                  </div>
                  <div style={{ fontSize: '12.5px', color: '#166534', marginTop: '4px' }}>
                    Total Processed: <strong>{importResult.total}</strong> · Created: <strong>{importResult.createdCount}</strong> members · Failed: <strong>{importResult.failedCount}</strong>
                  </div>
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div style={{ marginTop: '6px', fontSize: '12px', color: '#b91c1c' }}>
                      Errors: {importResult.errors.map((e: any, i: number) => <div key={i}>• {typeof e === 'string' ? e : JSON.stringify(e)}</div>)}
                    </div>
                  )}
                </div>
              )}

              <div className="admin-form__actions">
                <button
                  type="button"
                  onClick={() => setShowBulkImportModal(false)}
                  disabled={isImporting}
                  className="admin-btn admin-btn--secondary"
                >
                  {importResult ? 'Close' : 'Cancel'}
                </button>
                {!importResult && (
                  <button
                    type="button"
                    onClick={handleExecuteBulkImport}
                    disabled={isImporting || parsedImportRows.length === 0}
                    className="admin-btn admin-btn--primary"
                  >
                    {isImporting ? 'Importing…' : `Import ${parsedImportRows.length} Members`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Member Modal */}
      {editingUser && (
        <div className="admin-modal-overlay">
          <div className="admin-modal" style={{ maxWidth: '680px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
            <form onSubmit={handleSaveEditUser} className="admin-form">
              <h3 className="admin-form__title">Edit Member Profile · {editingUser.email}</h3>

              {editError && <div className="admin-alert admin-alert--error">{editError}</div>}

              {/* 1. Profile & Role */}
              <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--navy)', borderBottom: '1px solid var(--line)', paddingBottom: '6px', marginBottom: '12px' }}>
                1. Member Identity &amp; Role
              </div>

              <div className="admin-form__row">
                <div className="admin-form__group">
                  <label className="admin-form__label">First Name <span style={{ color: '#EF4444' }}>*</span></label>
                  <input
                    type="text"
                    required
                    value={editFirstName}
                    onChange={(e) => setEditFirstName(e.target.value)}
                    className="admin-form__input"
                  />
                </div>

                <div className="admin-form__group">
                  <label className="admin-form__label">Last Name <span style={{ color: '#EF4444' }}>*</span></label>
                  <input
                    type="text"
                    required
                    value={editLastName}
                    onChange={(e) => setEditLastName(e.target.value)}
                    className="admin-form__input"
                  />
                </div>
              </div>

              <div className="admin-form__row">
                <div className="admin-form__group">
                  <label className="admin-form__label">Employee ID</label>
                  <input
                    type="text"
                    value={editEmployeeId}
                    onChange={(e) => setEditEmployeeId(e.target.value)}
                    className="admin-form__input"
                  />
                </div>

                <div className="admin-form__group">
                  <label className="admin-form__label">Assigned Role</label>
                  <RoleSelector currentRole={editRole} onChange={setEditRole} />
                </div>
              </div>

              {/* 2. Organization & Reporting Manager */}
              <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--navy)', borderBottom: '1px solid var(--line)', paddingBottom: '6px', margin: '18px 0 12px' }}>
                2. Hierarchy &amp; Reporting Manager
              </div>

              <div className="admin-form__group">
                <label className="admin-form__label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  👔 Reporting Manager
                  <span style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 400 }}>(Supervisor for evaluations &amp; escalations)</span>
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <select
                    value={editManagerId}
                    onChange={(e) => handleEditManagerSelect(e.target.value)}
                    className="admin-form__select"
                  >
                    <option value="">— Select from Organization Managers —</option>
                    {eligibleManagers
                      .filter((m) => m.id !== editingUser.id)
                      .map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.firstName} {m.lastName} ({m.role} • {m.email})
                        </option>
                      ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Or enter Manager Name..."
                    value={editManagerName}
                    onChange={(e) => {
                      setEditManagerName(e.target.value);
                      if (editManagerId) setEditManagerId('');
                    }}
                    className="admin-form__input"
                  />
                </div>
              </div>

              <div className="admin-form__row">
                <div className="admin-form__group">
                  <label className="admin-form__label">Designation</label>
                  <select
                    value={editDesignation}
                    onChange={(e) => setEditDesignation(e.target.value)}
                    className="admin-form__select"
                  >
                    {availableDesignations.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="admin-form__group">
                  <label className="admin-form__label">Team / Unit</label>
                  <select
                    value={editTeam}
                    onChange={(e) => setEditTeam(e.target.value)}
                    className="admin-form__select"
                  >
                    {availableTeams.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="admin-form__row">
                <div className="admin-form__group">
                  <label className="admin-form__label">Region / Hub</label>
                  <select
                    value={editRegion}
                    onChange={(e) => setEditRegion(e.target.value)}
                    className="admin-form__select"
                  >
                    {availableRegions.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="admin-form__group">
                  <label className="admin-form__label">Seniority Grade</label>
                  <select
                    value={editGrade}
                    onChange={(e) => setEditGrade(e.target.value)}
                    className="admin-form__select"
                  >
                    {GRADE_OPTIONS.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 3. Custom Metadata Attributes (UDF) */}
              {activeUdfs.filter((u) => !u.isSystem && !['region', 'designation', 'managerName'].includes(u.code)).length > 0 && (
                <>
                  <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--navy)', borderBottom: '1px solid var(--line)', paddingBottom: '6px', margin: '18px 0 12px' }}>
                    3. Custom Metadata Attributes (UDF)
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                    {activeUdfs
                      .filter((u) => !u.isSystem && !['region', 'designation', 'managerName'].includes(u.code))
                      .map((udf) => (
                        <div key={udf.id} className="admin-form__group" style={{ margin: 0 }}>
                          <label className="admin-form__label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {udf.name}
                            {udf.required && <span style={{ color: '#EF4444', fontWeight: 800 }}>*</span>}
                            <span style={{ fontSize: '10.5px', color: 'var(--muted)', background: 'var(--surface)', padding: '1px 5px', borderRadius: '4px' }}>
                              {udf.category}
                            </span>
                          </label>

                          {udf.type === 'SINGLE_SELECT' || udf.type === 'MULTI_SELECT' ? (
                            <select
                              value={editCustomFieldValues[udf.code] || ''}
                              onChange={(e) => setEditCustomFieldValues({ ...editCustomFieldValues, [udf.code]: e.target.value })}
                              className="admin-form__select"
                            >
                              <option value="">Select {udf.name}...</option>
                              {udf.options.map((opt, i) => (
                                <option key={i} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          ) : udf.type === 'DATE' ? (
                            <input
                              type="date"
                              value={editCustomFieldValues[udf.code] || ''}
                              onChange={(e) => setEditCustomFieldValues({ ...editCustomFieldValues, [udf.code]: e.target.value })}
                              className="admin-form__input"
                            />
                          ) : udf.type === 'NUMBER' ? (
                            <input
                              type="number"
                              placeholder={`Enter ${udf.name}...`}
                              value={editCustomFieldValues[udf.code] || ''}
                              onChange={(e) => setEditCustomFieldValues({ ...editCustomFieldValues, [udf.code]: e.target.value })}
                              className="admin-form__input"
                            />
                          ) : udf.type === 'BOOLEAN' ? (
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', paddingTop: '6px' }}>
                              <input
                                type="checkbox"
                                checked={Boolean(editCustomFieldValues[udf.code])}
                                onChange={(e) => setEditCustomFieldValues({ ...editCustomFieldValues, [udf.code]: e.target.checked })}
                              />
                              Enable {udf.name}
                            </label>
                          ) : (
                            <input
                              type="text"
                              placeholder={`Enter ${udf.name}...`}
                              value={editCustomFieldValues[udf.code] || ''}
                              onChange={(e) => setEditCustomFieldValues({ ...editCustomFieldValues, [udf.code]: e.target.value })}
                              className="admin-form__input"
                            />
                          )}
                        </div>
                      ))}
                  </div>
                </>
              )}

              <div className="admin-form__actions">
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  disabled={isUpdating}
                  className="admin-btn admin-btn--secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUpdating || !editFirstName}
                  className="admin-btn admin-btn--primary"
                >
                  {isUpdating ? 'Saving…' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetTargetUser && (
        <div className="admin-modal-overlay">
          <div className="admin-modal" style={{ maxWidth: '560px', width: '90%' }}>
            <form onSubmit={handleExecuteResetPassword} className="admin-form">
              <h3 className="admin-form__title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🔑</span> Reset Member Password
              </h3>
              <p style={{ fontSize: '13.5px', color: 'var(--muted)', marginTop: '-8px', marginBottom: '16px' }}>
                Reset the access credentials for <strong>{resetTargetUser.firstName} {resetTargetUser.lastName}</strong> (<code>{resetTargetUser.email}</code>).
              </p>

              {/* Mode Toggle */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '18px', background: 'var(--surface)', padding: '4px', borderRadius: '8px' }}>
                <button
                  type="button"
                  onClick={() => { setResetMode('DIRECT'); setGeneratedResetUrl(''); }}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                    background: resetMode === 'DIRECT' ? '#ffffff' : 'transparent',
                    color: resetMode === 'DIRECT' ? '#2563eb' : 'var(--muted)',
                    boxShadow: resetMode === 'DIRECT' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  }}
                >
                  Set New Password Directly
                </button>
                <button
                  type="button"
                  onClick={() => { setResetMode('LINK'); }}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: 'none',
                    fontWeight: 600,
                    fontSize: '13px',
                    cursor: 'pointer',
                    background: resetMode === 'LINK' ? '#ffffff' : 'transparent',
                    color: resetMode === 'LINK' ? '#2563eb' : 'var(--muted)',
                    boxShadow: resetMode === 'LINK' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  }}
                >
                  Generate Reset Link
                </button>
              </div>

              {resetMode === 'DIRECT' ? (
                <div className="admin-form__group">
                  <label className="admin-form__label">
                    New Password <span className="admin-form__required">*</span>
                  </label>
                  <input
                    type="password"
                    required
                    value={resetNewPassword}
                    onChange={(e) => setResetNewPassword(e.target.value)}
                    placeholder="Enter new password (min 12 chars, upper/lower/number/symbol)..."
                    className="admin-form__input"
                    autoFocus
                  />
                  <span style={{ fontSize: '11.5px', color: 'var(--muted)', display: 'block', marginTop: '4px' }}>
                    Password policy requires at least 12 characters with uppercase, lowercase, numbers, and symbols. All previous sessions will be invalidated.
                  </span>
                </div>
              ) : (
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ fontSize: '13px', color: 'var(--ink)', lineHeight: '1.5' }}>
                    Click below to generate a secure, single-use 30-minute password reset link that you can copy and send directly to the learner.
                  </p>

                  {generatedResetUrl ? (
                    <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '12px', marginTop: '12px' }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                        Password Reset Link (Valid for 30 min):
                      </div>
                      <input
                        type="text"
                        readOnly
                        value={generatedResetUrl}
                        className="admin-form__input"
                        style={{ fontSize: '12px', background: '#ffffff', marginBottom: '8px', cursor: 'text' }}
                        onClick={(e) => (e.target as HTMLInputElement).select()}
                      />
                      <button
                        type="button"
                        onClick={handleCopyResetLink}
                        className="admin-btn admin-btn--primary admin-btn--sm"
                        style={{ width: '100%' }}
                      >
                        {copySuccess ? '✓ Copied to Clipboard!' : '📋 Copy Reset Link'}
                      </button>
                    </div>
                  ) : null}
                </div>
              )}

              <div className="admin-form__actions">
                <button
                  type="button"
                  onClick={() => setResetTargetUser(null)}
                  disabled={isResettingPassword}
                  className="admin-btn admin-btn--secondary"
                >
                  Close
                </button>
                {(!generatedResetUrl || resetMode === 'DIRECT') && (
                  <button
                    type="submit"
                    disabled={isResettingPassword || (resetMode === 'DIRECT' && !resetNewPassword.trim())}
                    className="admin-btn admin-btn--primary"
                  >
                    {isResettingPassword
                      ? 'Processing…'
                      : resetMode === 'DIRECT'
                      ? 'Update Password'
                      : 'Generate Reset Link'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

